export interface CodeExampleProfile {
    id: string;
    name: string;
    net: string;
}

interface CodeExampleParams {
    appId: string;
    apiKey: string;
    profiles?: CodeExampleProfile[];
}

export const generateJavaScriptExample = ({ appId, apiKey, profiles = [] }: CodeExampleParams): string => {
    const effectiveProfiles =
        profiles.length > 0 ? profiles : [{ id: 'your-profile-id', name: 'Your Profile', net: 'social' }];

    const firstProfile = effectiveProfiles[0];

    const profileCommentLines = effectiveProfiles.map((p) => `// [${p.net}] ${p.name} → ${p.id}`).join('\n');

    return `// Your connected profiles IDs:
${profileCommentLines}
//
// Change PROFILE_ID below to target a different profile.

const API_BASE_URL = 'https://api.swonkie.dev/v2';
const APP_ID = '${appId}';
const API_KEY = '${apiKey}';
const PROFILE_ID = '${firstProfile.id}';

async function main() {
  // Step 1: Create a media entry
  console.log('Step 1: Creating media entry...');
  const mediaResponse = await fetch(\`\${API_BASE_URL}/media\`, {
    method: 'POST',
    headers: {
      'X-API-ID': APP_ID,
      'X-API-KEY': API_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: 'my-image.jpg',
      destinationType: 'POST_MEDIA'
    })
  });
  if (!mediaResponse.ok) throw new Error('Failed to create media entry: ' + await mediaResponse.text());

  const { uploadUrl, id: mediaId } = await mediaResponse.json();
  console.log('Media entry created:', mediaId);

  // Step 2: Upload the file binary
  console.log('Step 2: Uploading file binary...');
  const fileBlob = await fetch('https://picsum.photos/800/600').then(r => r.blob());
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob' },
    body: fileBlob
  });
  if (!uploadResponse.ok) throw new Error('File upload failed: ' + await uploadResponse.text());
  console.log('File uploaded successfully.');

  // Step 3: Confirm the upload
  console.log('Step 3: Confirming upload...');
  const confirmResponse = await fetch(\`\${API_BASE_URL}/media/\${mediaId}\`, {
    method: 'PATCH',
    headers: { 'X-API-ID': APP_ID, 'X-API-KEY': API_KEY }
  });
  if (!confirmResponse.ok) throw new Error('Failed to confirm upload: ' + await confirmResponse.text());
  console.log('Upload confirmed.');

  // Step 4: Wait for media processing to complete
  console.log('Step 4: Waiting for media processing...');
  let media;
  let retries = 0;
  const MAX_RETRIES = 30;
  do {
    await new Promise(resolve => setTimeout(resolve, 2000));
    console.log(\`Polling media status... (attempt \${++retries}/\${MAX_RETRIES})\`);
    const mediaRes = await fetch(\`\${API_BASE_URL}/media/\${mediaId}\`, {
      headers: { 'X-API-ID': APP_ID, 'X-API-KEY': API_KEY }
    });
    media = await mediaRes.json();
  } while (media.status === 'PROCESSING' && retries < MAX_RETRIES);

  if (media.status !== 'SUCCESS') throw new Error('Media processing failed');
  console.log('Media processing complete.');

  // Step 5: Create the post
  console.log('Step 5: Creating post...');
  const postResponse = await fetch(\`\${API_BASE_URL}/posts\`, {
    method: 'POST',
    headers: {
      'X-API-ID': APP_ID,
      'X-API-KEY': API_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      type: 'MEDIA',
      profileIds: [PROFILE_ID],
      captions: [{ plainText: 'Check out this amazing photo! 🚀' }],
      medias: [{ mediaFiles: [{ mediaLibId: mediaId }] }],
      links: [],
      labelIds: []
    })
  });
  if (!postResponse.ok) throw new Error('Failed to create post: ' + await postResponse.text());

  const post = await postResponse.json();
  console.log('Post created:', post.id);

  // Step 6: Schedule the post to be published
  console.log('Step 6: Scheduling post for publishing...');
  const publishResponse = await fetch(\`\${API_BASE_URL}/posts/\${post.id}/stage/publishNow\`, {
    method: 'PATCH',
    headers: { 'X-API-ID': APP_ID, 'X-API-KEY': API_KEY }
  });
  if (!publishResponse.ok) throw new Error('Failed to publish post: ' + await publishResponse.text());

  console.log('Post scheduled to be published!', post.id);
}

main().catch(err => console.error('Error:', err.message));`;
};
